import { Box, Stack, Typography } from '@mui/material';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import {
  FunctionField,
  TextField,
  // TextInput,
  useGetMany,
  useListController,
  usePermissions,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { varAlpha } from '../../../../theme/styles';
import CGDatagrid from '../../components/CGDatagrid';
import { CgList } from '../../components/CgList';
import { Resources } from '../resource';
import dayjs from 'dayjs';
import { PermissionData } from '../../entities/permission.entity';
import BetDetail from './BetDetail';
import { CRASH_STATUS, GameIds, RocketBetTypeEnum } from 'src/configs/game.config';
import { TIME_FORMAT } from 'src/configs/common.config';
import { formatNumber } from 'src/utils/format-number';
import CopyBtn from 'src/components/button/CopyBtn';
import HistoryFilter from './components/HistoryFilter';
import { DownloadCSV } from 'src/components/button/DownloadCSV';

const GameHistory = () => {
  const refresh = useRefresh();
  const translate = useTranslate();

  const { filterValues } = useListController();
  const gameId: string = filterValues.games;
  console.log('gameId: ', gameId);
  const { data } = useGetMany(Resources.game, { ids: gameId ? [gameId] : [] });

  const isFutureGame = gameId === GameIds.C_FUTURE;
  const isBoGame = gameId === GameIds.C_BO;
  const isRocketGame = gameId === GameIds.H_ROCKET;
  const isTshowGame = gameId === GameIds.H_TSHOW;

  const { permissions } = usePermissions<PermissionData>();
  const checkPermission = keyBy(permissions?.permissions, 'resource');
  const isAdmin = !isEmpty(checkPermission['*']?.action);
  const requireAgencyFilterCondition = isAdmin
    ? !filterValues?.agencyIds || !filterValues?.games
    : !filterValues?.games;

  const buildBoColumes = () => {
    return [
      <TextField source="pair" label="module.game.pair" sortable={false} />,
      <TextField source="currency" label="module.game.wallet" sortable={false} />,
      <FunctionField
        label="module.game.amount"
        textAlign="left"
        minWidth={150}
        maxWidth={150}
        render={(record) => `${formatNumber(record.amount)}`}
      />,

      <TextField
        source="transactionType"
        label="module.game.orderType"
        minWidth={150}
        maxWidth={150}
        textAlign="left"
        sortable={false}
      />,
      <TextField
        source="openPrice"
        label="module.game.openPrice"
        minWidth={150}
        maxWidth={150}
        textAlign="left"
        sortable={false}
      />,
      <FunctionField
        label="module.game.exitPrice"
        textAlign="left"
        minWidth={150}
        maxWidth={150}
        render={(record) => (Number(record.exitPrice) > 0 ? record.exitPrice : '--')}
      />,
      <TextField
        source="result"
        label="module.game.result"
        sortable={false}
        // minWidth={120}
        // maxWidth={120}
      />,
    ];
  };

  const buildFutureColumes = () => {
    return [
      <TextField source="pair" label="module.game.pair" sortable={false} />,
      <TextField source="currency" label="module.game.wallet" sortable={false} />,
      <FunctionField
        label="module.game.orderPriceAndFee"
        textAlign="left"
        minWidth={150}
        maxWidth={150}
        render={(record) => `${formatNumber(record.amount)}`}
      />,
      <TextField
        source="transactionType"
        label="module.game.orderType"
        textAlign="left"
        minWidth={150}
        maxWidth={150}
        sortable={false}
      />,
      <TextField
        source="exitReason"
        label="module.game.closeType"
        textAlign="left"
        minWidth={150}
        maxWidth={150}
        sortable={false}
      />,
      <TextField
        source="amountWithoutFee"
        label="module.game.orderPrice"
        minWidth={150}
        maxWidth={150}
      />,
      <TextField
        source="fee"
        label="module.game.fee"
        sortable={false}
        textAlign="left"
        minWidth={100}
        maxWidth={100}
      />,
      <TextField source="pnl" label="module.game.pnl" minWidth={120} maxWidth={120} />,
    ];
  };

  const buildRocketColumes = () => {
    return [
      <FunctionField
        label="module.game.betType"
        textAlign="left"
        maxWidth={150}
        render={(record) => {
          if (record.type === RocketBetTypeEnum.TrenBall) {
            return 'Trenball';
          }

          return 'Classic';
        }}
      />,
      <TextField source="currency" label="module.game.wallet" textAlign="left" sortable={false} />,
      <FunctionField
        label="module.game.betAmount"
        textAlign="left"
        maxWidth={150}
        render={(record) => `${formatNumber(record.amount)}`}
      />,
      <FunctionField
        label="module.game.result"
        textAlign="left"
        maxWidth={150}
        render={(record) => {
          const isRoundFinished = record.crashRound?.status === CRASH_STATUS.End;
          const betCalculated = isRoundFinished || (record.reward?.gt(0) ?? false);

          if (!betCalculated) return '--';

          const reward = Number(record.reward);
          if (reward <= 0) return `Lose -${formatNumber(record.amount)} ${record.currency}`;
          return `Win +${formatNumber(reward)} ${record.currency}`;
        }}
      />,
    ];
  };

  const buildGameColumes = () => {
    if (isBoGame) {
      return buildBoColumes();
    }

    if (isFutureGame) {
      return buildFutureColumes();
    }

    if (isRocketGame || isTshowGame) {
      return buildRocketColumes();
    }

    return [
      <TextField source="bet" label="module.game.betType" textAlign="left" sortable={false} />,
      <TextField source="currency" label="module.game.wallet" textAlign="left" sortable={false} />,
      <FunctionField
        label="module.game.betAmount"
        textAlign="left"
        maxWidth={150}
        render={(record) => `${formatNumber(record.amount)}`}
      />,
      <TextField source="result" label="module.game.result" textAlign="left" sortable={false} />,
    ];
  };

  return (
    <Stack spacing={1} direction="column" style={{ width: '100%', marginTop: '20px' }}>
      <Typography variant="h5" sx={{ marginBottom: '10px' }}>
        {translate('resource.gameBetHistories')}
      </Typography>
      {requireAgencyFilterCondition ? (
        <CgList
          empty={false}
          filters={<HistoryFilter translate={translate} gameId={gameId} />}
          actions={
            gameId? myDownloadCSV(gameId) : false
          }
          pagination={false}
        >
          <Box
            sx={{
              mx: 2,
              mb: 2,
              height: 100,
              borderRadius: 2,
              bgcolor: (theme) => varAlpha(theme.vars.palette.grey['500Channel'], 0.04),
              border: (theme) => `dashed 1px ${theme.vars.palette.divider}`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography variant="body1">
              {isAdmin
                ? translate('module.game.blankState')
                : translate('module.game.blankStateAgency')}
            </Typography>
          </Box>
        </CgList>
      ) : (
        <CgList
          empty={false}
          filters={<HistoryFilter translate={translate} gameId={gameId} />}
          actions={
            gameId? myDownloadCSV(gameId) : false
          }
        >
          <CGDatagrid key={isFutureGame + ''} rowClick={false} bulkActionButtons={false}>
            <TextField
              source="id"
              label="resource.id"
              minWidth={isFutureGame ? 397 : 'auto'}
              maxWidth={397}
            />
            <FunctionField
              label="module.game.user"
              textAlign="left"
              minWidth={150}
              maxWidth={150}
              render={(record) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography sx={{ flex: 1 }}>{record.user}</Typography>
                  <CopyBtn text={record.playerId ?? record.userId} showMsg={false} />
                </Box>
              )}
            />
            {buildGameColumes()}

            <FunctionField
              source="time"
              label="module.game.time"
              minWidth={180}
              maxWidth={200}
              render={(record) => (record.time ? dayjs(record.time).format(TIME_FORMAT) : '')}
            />
            <FunctionField
              label="module.game.action"
              textAlign="left"
              render={(record) => <BetDetail order={record} gameId={gameId} />}
              minWidth="115px"
            />
          </CGDatagrid>
          {/*<Button*/}
          {/*  style={{*/}
          {/*    position: 'absolute',*/}
          {/*    bottom: -40,*/}
          {/*    left: 0,*/}
          {/*    zIndex: 1,*/}
          {/*    background: '#fff',*/}
          {/*    color: '#000',*/}
          {/*    height: 30,*/}
          {/*    paddingRight: 0,*/}
          {/*    paddingLeft: 10,*/}
          {/*    margin: 0,*/}
          {/*  }}*/}
          {/*  onClick={refresh}*/}
          {/*>*/}
          {/*  <>*/}
          {/*    <HiRefresh style={{ marginRight: 3 }} />*/}
          {/*    {translate('resource.refresh')}*/}
          {/*  </>*/}
          {/*</Button>*/}
        </CgList>
      )}
    </Stack>
  );
};
function myDownloadCSV(gameId :any){
  return(
    <DownloadCSV
      path="/game-detail/download"
      fileName={`game_detail_${gameId.toLowerCase()}`}
    />
  );
}

export default GameHistory;
